import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Clock, ChefHat } from 'lucide-react';
import axios from 'axios';
import { C, API } from './dashboardConstants';
import { scoreGoalFit, FitTag, fitContextLine } from '@/utils/useGoalFit';

export const SuggestedMealCard = ({ remaining, protRemaining, authHeaders, navigate }) => {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const fetchSuggestion = async () => {
      try {
        const res = await axios.get(`${API}/suggestions/what-to-eat?client_hour=${new Date().getHours()}`, authHeaders());
        // Only show curated recipe suggestions (they have recipe_id)
        const sug = res.data?.suggestions?.find(s => s.recipe_id) || res.data?.suggestions?.[0];
        if (sug) setSuggestion(sug);
      } catch {}
      setLoading(false);
    };
    fetchSuggestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !suggestion) return null;

  const imageUrl = suggestion.image_url || `/food-images/recipes/${suggestion.slug || suggestion.recipe_id}.png`;
  const remainingObj = { calories: remaining, protein: protRemaining, carbs: 0, fat: 0 };
  const fitResult = scoreGoalFit(remainingObj, { calories: suggestion.calories || 0, protein: suggestion.protein || 0, carbs: suggestion.carbs || 0, fat: suggestion.fat || 0 }, 'progress', 1);
  const fitContext = fitContextLine(fitResult, remainingObj);

  return (
    <>
      <div className="nuri-reveal nuri-d4" data-testid="suggested-meal-card" style={{ marginBottom: 16 }}>
        <div
          className="nuri-card-3d nuri-card-hover"
          onClick={() => {
            // Navigate directly to recipe detail if we have a recipe_id
            if (suggestion.recipe_id) {
              navigate(`/recipe/${suggestion.recipe_id}`);
            } else {
              setShowDetail(true);
            }
          }}
          data-testid="suggested-meal-tap"
          style={{ padding: 0, cursor: 'pointer', overflow: 'hidden' }}
        >
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            <div style={{ width: 90, minHeight: 90, flexShrink: 0, position: 'relative', overflow: 'hidden', borderRadius: '14px 0 0 14px' }}>
              <img src={imageUrl} alt={suggestion.name}
                onError={e => { e.target.src = '/food-images/elegant_dinner.png'; }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(1.1) brightness(1.05)' }} />
            </div>
            <div style={{ flex: 1, padding: '14px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: 11, letterSpacing: '0.5px', color: '#9A8F87', fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', margin: '0 0 4px', fontWeight: 500 }}>Suggested next meal</p>
              <p style={{ fontSize: 16, fontWeight: 500, color: C.text, margin: 0, fontFamily: "'Playfair Display', serif" }}>
                {suggestion.name}
              </p>
              <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 12, fontFamily: 'var(--font-sans)', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: C.accent }}>{suggestion.calories} cal</span>
                <span style={{ color: C.green }}>P:{suggestion.protein}g</span>
                <span style={{ color: C.gold }}>C:{suggestion.carbs}g</span>
                <FitTag fitLevel={fitResult.fitLevel} fitScore={fitResult.fitScore} compact />
              </div>
              {fitContext && (
                <p style={{ fontSize: 10, color: '#5A8C6A', margin: '3px 0 0', fontFamily: 'var(--font-sans)' }}>{fitContext}</p>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 11, fontFamily: 'var(--font-sans)' }}>
                {suggestion.total_time && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: C.muted }}>
                    <Clock size={10} /> {suggestion.total_time} min
                  </span>
                )}
                {suggestion.difficulty && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: C.muted }}>
                    <ChefHat size={10} /> {suggestion.difficulty}
                  </span>
                )}
                <span style={{ color: C.green, marginLeft: 'auto' }}>Tap to view recipe</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDetail && !suggestion.recipe_id && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-end sm:items-center justify-center" style={{ zIndex: 9999 }} onClick={() => setShowDetail(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-[#FDFBF7] z-10 p-0" onClick={e => e.stopPropagation()}>
            <div style={{ height: 180, position: 'relative', overflow: 'hidden' }}>
              <img src={imageUrl} alt={suggestion.name}
                onError={e => { e.target.src = '/food-images/elegant_dinner.png'; }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(1.1) brightness(1.05)' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #FDFBF7 0%, transparent 50%)' }} />
              <button onClick={() => setShowDetail(false)}
                style={{ position: 'absolute', top: 12, right: 12, width: 32, height: 32, borderRadius: 16, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', color: '#fff' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '0 20px 24px' }}>
              <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 400, color: C.text, margin: '0 0 8px' }}>{suggestion.name}</h2>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                {[
                  { label: 'Calories', val: suggestion.calories, color: C.accent, u: '' },
                  { label: 'Protein', val: suggestion.protein, color: C.green, u: 'g' },
                  { label: 'Carbs', val: suggestion.carbs, color: C.gold, u: 'g' },
                  { label: 'Fat', val: suggestion.fat || '\u2014', color: '#D4736A', u: suggestion.fat ? 'g' : '' },
                ].map(m => (
                  <div key={m.label} style={{ flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 12, background: `${m.color}08` }}>
                    <p style={{ fontSize: 18, fontWeight: 700, color: m.color, margin: 0, fontFamily: 'var(--font-sans)' }}>{m.val}{m.u}</p>
                    <p style={{ fontSize: 10, color: C.muted, margin: 0 }}>{m.label}</p>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button data-testid="suggestion-log-btn" onClick={() => { navigate('/log-meal', { state: { prefillMeal: { name: suggestion.name, calories: suggestion.calories, protein: suggestion.protein, carbs: suggestion.carbs, fat: suggestion.fat || 0 } } }); setShowDetail(false); }}
                  style={{ flex: 1, padding: '14px 0', borderRadius: 14, background: C.green, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-sans)' }}>
                  Log This Meal
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
